import { useRef, useState, useEffect } from 'react';
import { DayPicker, CaptionProps, Matcher, useNavigation } from 'react-day-picker';
import cn from 'classnames';
import { enUS, ru, tr, zhCN } from 'date-fns/locale';
import { format, startOfDay } from 'date-fns';
import { formatDate } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import DropdownSimple from "@components/DropdownSimple";
import TextInput from "@components/TextInput/TextInput";
import { P2 } from "@components/new/Typo";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import CalendarIcon from "@assets/icons/outline/calendar-days.svg?react";
import chevronLeft from '@assets/icons/outline/chevron-left.svg?react';
import chevronRight from '@assets/icons/outline/chevron-right.svg?react';
import styles from './DateInput.module.scss';
import 'react-day-picker/dist/style.css';

type InputValueType = undefined | Date

interface IDateInput {
  className?: string;
  onChange: (value: (InputValueType)) => void;
  placeholder?: string;
  value: InputValueType;
  disabled?: boolean;
  disabledBefore?: Date;
  disabledAfter?: Date;
  hasError?: boolean;
  errorMessage?: string;
  title?: string;
}

const locales = {
  cn: zhCN,
  en: enUS,
  ru: ru,
  tr: tr,
};

const DateInput: React.FC<IDateInput> = (props: IDateInput) => {
  const {
    className,
    onChange,
    placeholder,
    value,
    disabled,
    disabledBefore,
    disabledAfter,
    hasError,
    errorMessage,
    title,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [startDate, setStartDate_] = useState<InputValueType>(undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = getCurrentLanguage();

  const setStartDate = (v: InputValueType) => setStartDate_(v ? startOfDay(v) : v);

  const onSelect = (v?: Date) => {
    onChange(v);
    setStartDate(v);
    setIsOpen(false);
  }

  useEffect(() => {
    setStartDate(value);
  }, [value])

  return (
    <div className={cn(styles.dateInput, className)} ref={dropdownRef}>
      <DropdownSimple
        triggerContent={(
          <TextInput
            className={styles.input}
            type="text"
            value={startDate ? formatDate(startDate, 'eeee, dd.MM') : ""}
            placeholder={placeholder}
            onChange={() => {}}
            prefix={<CalendarIcon />}
            onFocus={() => setIsOpen(true)}
            onClick={(e: any) => {
              e.stopPropagation();
            }}
            disabled={disabled}
            hasError={hasError}
            errorMessage={errorMessage}
            title={title}
          />
        )}
        dropdownContent={(
          <div className={styles.dropdown}>
            <DayPicker
              disabled={(disabledBefore || disabledAfter) ? ({
                ...(disabledBefore ? { before: disabledBefore } : {}),
                ...(disabledAfter ? { after: disabledAfter } : {}),
              } as Matcher) : undefined}
              locale={locales[currentLanguage]}
              mode="single"
              onSelect={onSelect}
              selected={new Date(value!)}
              components={{
                Caption: CustomCaptionComponent
              }}
              numberOfMonths={1}
            />
          </div>
        )}
        menuAlign="left"
        menuPosition="bottom"
        disabled={disabled}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </div>
  );
};

function CustomCaptionComponent(props: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const currentLanguage = getCurrentLanguage();

  return (
    <div className={styles.calendarCaption} key={String(props.displayMonth)}>
      <IconButton
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        size={IconButtonSize.L}
        type={IconButtonType.ICON}
        icon={chevronLeft}
      />
      <P2 className={styles.calendarCaptionLabel}>{format(props.displayMonth, "LLLL yyy", { locale: locales[currentLanguage] })}</P2>
      <IconButton
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        size={IconButtonSize.L}
        type={IconButtonType.ICON}
        icon={chevronRight}
      />
    </div>
  );
}

export default DateInput;
